<div class="page">
  <div class="header">
    <h1 class="header-title">
      <mat-icon>account_circle</mat-icon>
      Account
    </h1>
  </div>
  <mat-card class="content account-options-wrapper" aria-label="Accountgegevens">
    <form [formGroup]="contactForm" (ngSubmit)="saveChanges()">
      <div class="profile-layout">
        <div class="identity-block">
          @if (photoEnabled()) {
            <section class="profile-section photo-section" aria-labelledby="profile-photo-title">
              <h2 id="profile-photo-title">Profielfoto</h2>
              <div class="photo-editor">
                <div
                  class="upload-preview"
                  role="button"
                  tabindex="0"
                  aria-label="Profielfoto uploaden"
                  matTooltip="Klik om een profielfoto te uploaden"
                  matTooltipClass="wide-tooltip"
                  (click)="photoInput.click()"
                  (keydown.enter)="photoInput.click()"
                  (keydown.space)="$event.preventDefault(); photoInput.click()"
                >
                  @if (photo.photoPreviewUrl() && !photo.photoRemoved() && !imageFailed()) {
                    <img [src]="photo.photoPreviewUrl()" alt="Profielfoto" (error)="imageFailed.set(true)" />
                  } @else {
                    <div class="circle-icon primary-bg" aria-hidden="true">
                      {{ getInitial(profile()?.name) }}
                    </div>
                  }
                </div>
                <div class="photo-controls">
                  <input
                    #photoInput
                    hidden
                    type="file"
                    accept="image/*"
                    aria-label="Profielfoto selecteren"
                    (change)="selectPhoto($event)"
                    [disabled]="savingPhoto()"
                  />
                  <div class="action-row">
                    <button mat-stroked-button type="button" (click)="photoInput.click()" [disabled]="savingPhoto()">
                      <mat-icon>add_a_photo</mat-icon>{{ profile()?.photoUrl ? 'Foto vervangen' : 'Foto toevoegen' }}
                    </button>
                    @if ((photo.photoPreviewUrl() || profile()?.photoUrl) && !photo.photoRemoved()) {
                      <button
                        mat-flat-button
                        class="secondary-color"
                        type="button"
                        aria-label="Profielfoto verwijderen"
                        (click)="photo.remove()"
                        [disabled]="savingPhoto()"
                      >
                        <mat-icon>delete_outline</mat-icon>
                        Verwijderen
                      </button>
                    }
                  </div>
                  @if (photo.photoRemoved()) {
                    <p class="photo-pending-removal" role="status">
                      De profielfoto wordt verwijderd zodra je op ‘Gegevens opslaan’ drukt.
                    </p>
                  }
                </div>
              </div>
              @if (photo.photoValidationError() || photoError()) {
                <p class="profile-error" role="alert">{{ photo.photoValidationError() || photoError() }}</p>
              }
            </section>
          }
          @if (personalDataEnabled()) {
            <section class="profile-section" aria-labelledby="personal-details-title">
              <div class="section-heading">
                <h2 id="personal-details-title">Persoonsgegevens</h2>
              </div>
              <div class="personal-details">
                <div class="personal-name">
                  <h4>Naam</h4>
                  <p>{{ profile()?.name || 'Onbekend' }}</p>
                </div>
                <fieldset [disabled]="savingContact()" class="contact-fields">
                  <legend class="visually-hidden">Telefoonnummer</legend>
                  <mat-form-field class="phone-field">
                    <mat-label>Telefoonnummer</mat-label>
                    <input matInput formControlName="phone" type="tel" autocomplete="tel" required />
                    @if (contactForm.controls.phone.hasError('required')) {
                      <mat-error>Dit veld is verplicht.</mat-error>
                    }
                    @if (contactForm.controls.phone.hasError('phoneValidator')) {
                      <mat-error>Telefoonnummer is ongeldig.</mat-error>
                    }
                  </mat-form-field>
                </fieldset>
                <div class="readonly-details">
                  <div>
                    <h4>Startdatum</h4>
                    <p>{{ displayDate(profile()?.startDate) }}</p>
                  </div>
                  <div>
                    <h4>VOG-datum</h4>
                    <p>{{ displayDate(profile()?.vogDate) }}</p>
                  </div>
                  <div>
                    <h4>Rijtestdatum</h4>
                    <p>{{ displayDate(profile()?.driversTestDate) }}</p>
                  </div>
                </div>
              </div>
            </section>
          }
        </div>
        @if (personalDataEnabled()) {
          <div class="profile-section address-block" role="region" aria-labelledby="address-details-title">
            <h2 id="address-details-title">Adresgegevens</h2>
            <fieldset [disabled]="savingContact()" class="contact-fields">
              <legend class="visually-hidden">Adresgegevens</legend>
              <div class="address-numbers">
                <mat-form-field>
                  <mat-label>Postcode</mat-label>
                  <input matInput formControlName="postal" autocomplete="postal-code" required />
                  @if (contactForm.controls.postal.hasError('required')) {
                    <mat-error>Dit veld is verplicht.</mat-error>
                  }
                  @if (contactForm.controls.postal.hasError('postalValidator')) {
                    <mat-error>Postcode is ongeldig.</mat-error>
                  }
                </mat-form-field>
                <mat-form-field>
                  <mat-label>Huisnummer</mat-label>
                  <input matInput formControlName="houseNumber" inputmode="numeric" required />
                  @if (contactForm.controls.houseNumber.hasError('required')) {
                    <mat-error>Dit veld is verplicht.</mat-error>
                  }
                  @if (contactForm.controls.houseNumber.hasError('numberInput')) {
                    <mat-error>Huisnummer is ongeldig.</mat-error>
                  }
                </mat-form-field>
                <mat-form-field>
                  <mat-label>Toevoeging</mat-label>
                  <input matInput formControlName="houseNumberAddition" />
                  <mat-hint>Optioneel</mat-hint>
                </mat-form-field>
              </div>
              <div class="address-street">
                <mat-form-field>
                  <mat-label>Straatnaam</mat-label>
                  <input matInput formControlName="streetName" autocomplete="address-line1" required />
                  @if (address.isLookingUp()) {
                    <div matSuffix class="lookup-spinner">
                      <mat-spinner diameter="20" />
                    </div>
                  }
                  @if (contactForm.controls.streetName.hasError('required')) {
                    <mat-error>Dit veld is verplicht.</mat-error>
                  }
                </mat-form-field>
                <mat-form-field>
                  <mat-label>Woonplaats</mat-label>
                  <input matInput formControlName="city" autocomplete="address-level2" required />
                  @if (address.isLookingUp()) {
                    <div matSuffix class="lookup-spinner">
                      <mat-spinner diameter="20" />
                    </div>
                  }
                  @if (contactForm.controls.city.hasError('required')) {
                    <mat-error>Dit veld is verplicht.</mat-error>
                  }
                </mat-form-field>
              </div>
              @if (address.addressNotFound()) {
                <p class="address-warning" role="status">
                  <mat-icon>warning</mat-icon>
                  <span>
                    Adres niet gevonden. Controleer of de postcode en het huisnummer correct zijn ingevuld of vul
                    handmatig de straatnaam en woonplaats in.
                  </span>
                </p>
              }
            </fieldset>
          </div>
        }
      </div>
      @if (personalDataEnabled()) {
        @if (contactError()) {
          <p class="profile-error" role="alert">{{ contactError() }}</p>
        }
      }
    </form>
    @if (personalDataEnabled() || photoEnabled()) {
      <div class="action-row form-actions">
        <button
          mat-flat-button
          type="button"
          (click)="saveChanges()"
          [disabled]="!canSaveChanges()"
          [class.spinner]="savingContact() || savingPhoto()"
        >
          <mat-icon>save</mat-icon>Gegevens opslaan
        </button>
      </div>
    }
    <section class="profile-section security-section" role="region" aria-labelledby="account-security-title">
      <h2 id="account-security-title">Accountbeveiliging</h2>
      <button
        mat-flat-button
        class="account-security-button"
        aria-label="Wachtwoord wijzigen"
        [class.spinner]="sendingReset()"
        [disabled]="sendingReset()"
        (click)="sendPasswordReset()"
      >
        <mat-icon>lock</mat-icon>
        Wachtwoord wijzigen
      </button>
    </section>
  </mat-card>
</div>
